import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Send, Loader2, Bot, User, X, Maximize2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

// Floating "Ask AI" button + slide-over quick-chat available on every page.
// Reuses a single rolling session while mounted; full history lives on /ask-claude.
export default function ClaudeFab() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [sid, setSid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, sending, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    let id = sid;
    if (!id) {
      try { const { data } = await api.post("/assistant/sessions", {}); id = data.session_id; setSid(id); }
      catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return; }
    }
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post(`/assistant/sessions/${id}/chat`, { message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "The assistant could not respond.");
      setMessages((m) => m.slice(0, -1)); setInput(msg);
    } finally { setSending(false); }
  };

  const onKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <>
      {/* Floating button */}
      <button data-testid="claude-fab" onClick={() => setOpen((o) => !o)} title="Ask AI"
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-xl transition-transform hover:scale-105 active:scale-95">
        {open ? <X className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
      </button>

      {/* Slide-over panel */}
      {open && (
        <div data-testid="claude-fab-panel" className="fixed bottom-24 right-6 z-50 flex h-[60vh] w-[min(92vw,380px)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-slate-100 bg-gradient-to-br from-indigo-500 to-violet-600 px-4 py-3 text-white">
            <div className="flex items-center gap-2"><Sparkles className="h-4 w-4" /><span className="text-sm font-semibold">Ask AI</span></div>
            <button data-testid="claude-fab-expand" onClick={() => { setOpen(false); navigate("/ask-claude"); }} title="Open full page" className="rounded p-1 hover:bg-white/15">
              <Maximize2 className="h-4 w-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-3" data-testid="claude-fab-messages">
            {messages.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center px-4 text-center">
                <Bot className="h-8 w-8 text-primary" />
                <p className="mt-2 text-sm font-medium text-slate-700">Quick questions about your data</p>
                <p className="mt-1 text-xs text-slate-400">Read-only — projects, quotes, DIDs &amp; activity.</p>
              </div>
            ) : messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${m.role === "user" ? "bg-primary/15 text-primary" : "bg-primary text-primary-foreground"}`}>
                  {m.role === "user" ? <User className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
                </span>
                <div className={`max-w-[80%] whitespace-pre-wrap rounded-xl px-3 py-2 text-[13px] leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-slate-100 text-slate-800"}`}>{m.content}</div>
              </div>
            ))}
            {sending && <div className="flex gap-2"><span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary text-primary-foreground"><Bot className="h-3.5 w-3.5" /></span><div className="rounded-xl bg-slate-100 px-3 py-2"><Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" /></div></div>}
          </div>

          <div className="border-t border-slate-100 p-2.5">
            <div className="flex items-end gap-2">
              <Textarea data-testid="claude-fab-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKeyDown} rows={1} placeholder="Ask AI…" className="max-h-24 min-h-[40px] resize-none text-sm" />
              <Button data-testid="claude-fab-send" onClick={() => send()} disabled={sending || !input.trim()} size="icon" className="h-10 w-10 shrink-0 bg-primary hover:bg-primary/90 text-primary-foreground">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
